import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type AssetShellRecord,
  type AssetTreeNode,
  type TenantRecord,
  createAsset,
  createTenant,
  fetchAssetTree,
  listTenants,
} from './estateClient';
import { tokens } from '@/design';
import './PlatformLanding.css';

type ScreenId =
  | 'overview'
  | 'installation'
  | 'domains'
  | 'assets'
  | 'layers'
  | 'viewer'
  | 'projects'
  | 'handover';

interface DrawerItem {
  id: ScreenId;
  icon: string;
  label: string;
}

const DRAWER_ITEMS: DrawerItem[] = [
  { id: 'overview', icon: 'dashboard', label: 'Overview' },
  { id: 'installation', icon: 'apartment', label: 'Tenant installation' },
  { id: 'domains', icon: 'groups', label: 'Domains and users' },
  { id: 'assets', icon: 'account_tree', label: 'Asset hierarchy' },
  { id: 'layers', icon: 'layers', label: 'gTwins and layers' },
  { id: 'viewer', icon: 'visibility', label: 'User viewer' },
  { id: 'projects', icon: 'folder_open', label: 'Analysis projects' },
  { id: 'handover', icon: 'assignment', label: 'Engineer handover' },
];

const HEADER_TABS: DrawerItem[] = [
  { id: 'overview', icon: 'dashboard', label: 'Overview' },
  { id: 'installation', icon: 'apartment', label: 'Tenants' },
  { id: 'assets', icon: 'account_tree', label: 'Assets' },
  { id: 'projects', icon: 'folder_open', label: 'Projects' },
];

const LOR_BAND_COLORS = [
  tokens.color.semantic.info,
  tokens.color.semantic.success,
  tokens.color.semantic.warning,
  tokens.color.engine.deckgl,
  tokens.color.engine.three,
  tokens.color.semantic.danger,
] as const;

const LOR_BANDS = [
  { l: 0, n: 'Region / portfolio', color: LOR_BAND_COLORS[0] },
  { l: 1, n: 'Business area', color: LOR_BAND_COLORS[1] },
  { l: 2, n: 'Field / location', color: LOR_BAND_COLORS[2] },
  { l: 3, n: 'Addressable shell', color: LOR_BAND_COLORS[3] },
  { l: 4, n: 'Optional shell', color: LOR_BAND_COLORS[4] },
  { l: 5, n: 'Layer-owned', color: LOR_BAND_COLORS[5] },
];

/* ── Domain / User types ───────────────────────────────────────── */

interface DomainRecord {
  id: string;
  tenantCode: string;
  name: string;
  profile: string;
  owner: string;
  crs: string;
}

interface UserRecord {
  id: string;
  tenantCode: string;
  name: string;
  email: string;
  role: string;
  domain: string;
}

/* ── Sample data (embedded from V5 CSV) ────────────────────────── */

interface SampleAsset {
  id: string;
  parentId: string;
  label: string;
  lor: number;
  nodeType: string;
  path: string;
}

interface SampleTenant {
  code: string;
  label: string;
  region: string;
  classification: string;
  units: string;
  assets: SampleAsset[];
  domains: Omit<DomainRecord, 'tenantCode'>[];
  users: Omit<UserRecord, 'tenantCode'>[];
}

const ADNOC_ASSETS: SampleAsset[] = [
  { id: 'asset-adnoc-offshore', parentId: '', label: 'ADNOC Offshore', lor: 0, nodeType: 'portfolio', path: 'ADNOC Offshore' },
  { id: 'asset-adnoc-upper-zakum', parentId: 'asset-adnoc-offshore', label: 'Upper Zakum Field', lor: 2, nodeType: 'field', path: 'ADNOC Offshore / Upper Zakum Field' },
  { id: 'asset-adnoc-lower-zakum', parentId: 'asset-adnoc-offshore', label: 'Lower Zakum Field', lor: 2, nodeType: 'field', path: 'ADNOC Offshore / Lower Zakum Field' },
  { id: 'asset-adnoc-umm-shaif', parentId: 'asset-adnoc-offshore', label: 'Umm Shaif Field', lor: 2, nodeType: 'field', path: 'ADNOC Offshore / Umm Shaif Field' },
  { id: 'asset-adnoc-satah', parentId: 'asset-adnoc-offshore', label: 'Satah Field', lor: 2, nodeType: 'field', path: 'ADNOC Offshore / Satah Field' },
  { id: 'asset-adnoc-nasr', parentId: 'asset-adnoc-offshore', label: 'Nasr Field', lor: 2, nodeType: 'field', path: 'ADNOC Offshore / Nasr Field' },
  { id: 'asset-adnoc-umm-al-dalkh', parentId: 'asset-adnoc-offshore', label: 'Umm Al Dalkh Field', lor: 2, nodeType: 'field', path: 'ADNOC Offshore / Umm Al Dalkh Field' },
  { id: 'asset-adnoc-sarb', parentId: 'asset-adnoc-offshore', label: 'SARB Field', lor: 2, nodeType: 'field', path: 'ADNOC Offshore / SARB Field' },
  { id: 'asset-adnoc-umm-lulu', parentId: 'asset-adnoc-offshore', label: 'Umm Lulu Field', lor: 2, nodeType: 'field', path: 'ADNOC Offshore / Umm Lulu Field' },
  { id: 'asset-adnoc-abu-al-bukhoosh', parentId: 'asset-adnoc-offshore', label: 'Abu Al Bukhoosh Field', lor: 2, nodeType: 'field', path: 'ADNOC Offshore / Abu Al Bukhoosh Field' },
  { id: 'asset-adnoc-das-island', parentId: 'asset-adnoc-offshore', label: 'Das Island Terminal', lor: 2, nodeType: 'terminal', path: 'ADNOC Offshore / Das Island Terminal' },
  { id: 'asset-adnoc-zirku-island', parentId: 'asset-adnoc-offshore', label: 'Zirku Island Terminal', lor: 2, nodeType: 'terminal', path: 'ADNOC Offshore / Zirku Island Terminal' },
  { id: 'asset-adnoc-upper-zakum-islands', parentId: 'asset-adnoc-upper-zakum', label: 'Upper Zakum Artificial Islands', lor: 3, nodeType: 'artificial-island-group', path: 'ADNOC Offshore / Upper Zakum Field / Upper Zakum Artificial Islands' },
  { id: 'asset-adnoc-nasr-super-complex', parentId: 'asset-adnoc-nasr', label: 'Nasr Super Complex', lor: 3, nodeType: 'complex-container', path: 'ADNOC Offshore / Nasr Field / Nasr Super Complex' },
  { id: 'asset-adnoc-nasr-cpp', parentId: 'asset-adnoc-nasr-super-complex', label: 'Nasr CPP', lor: 3, nodeType: 'platform', path: 'ADNOC Offshore / Nasr Field / Nasr Super Complex / Nasr CPP' },
  { id: 'asset-adnoc-nasr-whp-1', parentId: 'asset-adnoc-nasr-super-complex', label: 'Nasr WHP-1', lor: 3, nodeType: 'platform', path: 'ADNOC Offshore / Nasr Field / Nasr Super Complex / Nasr WHP-1' },
  { id: 'asset-adnoc-umm-shaif-gas-platform', parentId: 'asset-adnoc-umm-shaif', label: 'Umm Shaif Gas Platform', lor: 3, nodeType: 'platform', path: 'ADNOC Offshore / Umm Shaif Field / Umm Shaif Gas Platform' },
  { id: 'asset-adnoc-umm-lulu-super-complex', parentId: 'asset-adnoc-umm-lulu', label: 'Umm Lulu Super Complex', lor: 3, nodeType: 'complex-container', path: 'ADNOC Offshore / Umm Lulu Field / Umm Lulu Super Complex' },
];

const NOC_QATAR_ASSETS: SampleAsset[] = [
  { id: 'asset-noc-portfolio', parentId: '', label: 'North Oil Company Qatar Portfolio', lor: 0, nodeType: 'portfolio', path: 'North Oil Company Qatar Portfolio' },
  { id: 'asset-noc-al-shaheen-operations', parentId: 'asset-noc-portfolio', label: 'Al Shaheen Operations', lor: 1, nodeType: 'business-unit', path: 'North Oil Company Qatar / Al Shaheen Operations' },
  { id: 'asset-noc-al-shaheen-field', parentId: 'asset-noc-al-shaheen-operations', label: 'Al Shaheen Field', lor: 2, nodeType: 'field', path: 'Al Shaheen Operations / Al Shaheen Field' },
  { id: 'asset-noc-al-shaheen-platform-fleet', parentId: 'asset-noc-al-shaheen-field', label: 'Al Shaheen Platform Fleet', lor: 3, nodeType: 'platform-fleet', path: 'Al Shaheen Field / Al Shaheen Platform Fleet' },
  { id: 'asset-noc-gallaf-development', parentId: 'asset-noc-al-shaheen-field', label: 'Gallaf Development', lor: 3, nodeType: 'development-package', path: 'Al Shaheen Field / Gallaf Development' },
  { id: 'asset-noc-gallaf-batch-1', parentId: 'asset-noc-gallaf-development', label: 'Gallaf Batch 1', lor: 3, nodeType: 'development-package', path: 'Al Shaheen Field / Gallaf Development / Gallaf Batch 1' },
  { id: 'asset-noc-gallaf-batch-2', parentId: 'asset-noc-gallaf-development', label: 'Gallaf Batch 2', lor: 3, nodeType: 'development-package', path: 'Al Shaheen Field / Gallaf Development / Gallaf Batch 2' },
  { id: 'asset-noc-gallaf-batch-3', parentId: 'asset-noc-gallaf-development', label: 'Gallaf Batch 3', lor: 3, nodeType: 'development-package', path: 'Al Shaheen Field / Gallaf Development / Gallaf Batch 3' },
  { id: 'asset-noc-ruya-project', parentId: 'asset-noc-al-shaheen-field', label: 'Ruya Project', lor: 3, nodeType: 'development-package', path: 'Al Shaheen Field / Ruya Project' },
];

const ONGC_ASSETS: SampleAsset[] = [
  { id: 'asset-ongc-portfolio', parentId: '', label: 'ONGC Portfolio', lor: 0, nodeType: 'portfolio', path: 'ONGC Portfolio' },
  { id: 'asset-ongc-offshore', parentId: 'asset-ongc-portfolio', label: 'ONGC Offshore Assets', lor: 1, nodeType: 'business-unit', path: 'ONGC Portfolio / ONGC Offshore Assets' },
  { id: 'asset-ongc-mumbai-offshore-basin', parentId: 'asset-ongc-offshore', label: 'Mumbai Offshore Basin', lor: 2, nodeType: 'basingroup', path: 'ONGC Offshore / Mumbai Offshore Basin' },
  { id: 'asset-ongc-mumbai-high', parentId: 'asset-ongc-mumbai-offshore-basin', label: 'Mumbai High Field', lor: 2, nodeType: 'field', path: 'Mumbai Offshore Basin / Mumbai High Field' },
  { id: 'asset-ongc-mumbai-high-north', parentId: 'asset-ongc-mumbai-high', label: 'Mumbai High North', lor: 3, nodeType: 'field-sector', path: 'Mumbai High Field / Mumbai High North' },
  { id: 'asset-ongc-mumbai-high-south', parentId: 'asset-ongc-mumbai-high', label: 'Mumbai High South', lor: 3, nodeType: 'field-sector', path: 'Mumbai High Field / Mumbai High South' },
  { id: 'asset-ongc-neelam-heera', parentId: 'asset-ongc-mumbai-offshore-basin', label: 'Neelam and Heera Fields', lor: 2, nodeType: 'field-group', path: 'Mumbai Offshore Basin / Neelam and Heera Fields' },
  { id: 'asset-ongc-bassein-satellite', parentId: 'asset-ongc-mumbai-offshore-basin', label: 'Bassein and Satellite Fields', lor: 2, nodeType: 'field-group', path: 'Mumbai Offshore Basin / Bassein and Satellite Fields' },
  { id: 'asset-ongc-kg-dwn-98-2', parentId: 'asset-ongc-offshore', label: 'KG-DWN-98/2 Block', lor: 2, nodeType: 'offshore-block', path: 'ONGC Offshore / KG-DWN-98/2 Block' },
  { id: 'asset-ongc-kg-cluster-2a', parentId: 'asset-ongc-kg-dwn-98-2', label: 'KG-DWN-98/2 Cluster 2A', lor: 3, nodeType: 'cluster', path: 'KG-DWN-98/2 Block / Cluster 2A' },
  { id: 'asset-ongc-kg-cluster-2b', parentId: 'asset-ongc-kg-dwn-98-2', label: 'KG-DWN-98/2 Cluster 2B', lor: 3, nodeType: 'cluster', path: 'KG-DWN-98/2 Block / Cluster 2B' },
  { id: 'asset-ongc-kg-gas-process-platform', parentId: 'asset-ongc-kg-cluster-2b', label: 'KG-DWN-98/2 Gas Process Platform', lor: 3, nodeType: 'platform', path: 'KG-DWN-98/2 Cluster 2B / Gas Process Platform' },
  { id: 'asset-ongc-kg-fpso', parentId: 'asset-ongc-kg-cluster-2a', label: 'KG-DWN-98/2 Cluster 2A FPSO', lor: 3, nodeType: 'floating-production-asset', path: 'KG-DWN-98/2 Cluster 2A / FPSO' },
];

const ADNOC_DOMAINS: Omit<DomainRecord, 'tenantCode'>[] = [
  { id: 'dom-adnoc-offshore', name: 'ADNOC Offshore', profile: 'asset', owner: 'Ahmed Al Mazrouei', crs: 'EPSG:32640' },
  { id: 'dom-adnoc-plant-engineering', name: 'Plant Engineering', profile: 'engineering', owner: 'Khalid Al Hammadi', crs: 'linear' },
  { id: 'dom-adnoc-subsea', name: 'Subsea Operations', profile: 'subsea', owner: 'Fatima Al Dhaheri', crs: 'EPSG:32640' },
  { id: 'dom-adnoc-geospatial', name: 'Geospatial Services', profile: 'geospatial', owner: 'Omar Al Suwaidi', crs: 'EPSG:4326' },
  { id: 'dom-adnoc-integrity', name: 'Asset Integrity', profile: 'engineering', owner: 'Mariam Al Ketbi', crs: 'linear' },
];

const ADNOC_USERS: Omit<UserRecord, 'tenantCode'>[] = [
  { id: 'usr-adnoc-admin', name: 'Ahmed Al Mazrouei', email: 'ahmed.mazrouei@adnoc.example', role: 'tenant.admin', domain: 'ADNOC Offshore' },
  { id: 'usr-adnoc-eng-lead', name: 'Khalid Al Hammadi', email: 'khalid.hammadi@adnoc.example', role: 'domain.owner', domain: 'Plant Engineering' },
  { id: 'usr-adnoc-subsea-lead', name: 'Fatima Al Dhaheri', email: 'fatima.dhaheri@adnoc.example', role: 'domain.owner', domain: 'Subsea Operations' },
  { id: 'usr-adnoc-gis-lead', name: 'Omar Al Suwaidi', email: 'omar.suwaidi@adnoc.example', role: 'domain.owner', domain: 'Geospatial Services' },
  { id: 'usr-adnoc-integrity-mgr', name: 'Mariam Al Ketbi', email: 'mariam.ketbi@adnoc.example', role: 'domain.owner', domain: 'Asset Integrity' },
  { id: 'usr-adnoc-nasr-eng', name: 'Saeed Al Mansoori', email: 'saeed.mansoori@adnoc.example', role: 'project.creator', domain: 'Plant Engineering' },
  { id: 'usr-adnoc-zakum-analyst', name: 'Noura Al Shamsi', email: 'noura.shamsi@adnoc.example', role: 'project.editor', domain: 'ADNOC Offshore' },
  { id: 'usr-adnoc-subsea-eng', name: 'Rashid Al Blooshi', email: 'rashid.blooshi@adnoc.example', role: 'project.creator', domain: 'Subsea Operations' },
  { id: 'usr-adnoc-viewer', name: 'Layla Al Hashemi', email: 'layla.hashemi@adnoc.example', role: 'project.viewer', domain: 'Geospatial Services' },
  { id: 'usr-adnoc-asset-mgr', name: 'Hamdan Al Nuaimi', email: 'hamdan.nuaimi@adnoc.example', role: 'asset.manager', domain: 'ADNOC Offshore' },
];

const NOC_DOMAINS: Omit<DomainRecord, 'tenantCode'>[] = [
  { id: 'dom-noc-asset-registry', name: 'Asset Registry', profile: 'asset', owner: 'Mohammed Al Thani', crs: 'EPSG:32639' },
  { id: 'dom-noc-plant-engineering', name: 'Plant Engineering', profile: 'engineering', owner: 'Jean-Pierre Dupont', crs: 'linear' },
  { id: 'dom-noc-subsea', name: 'Subsea', profile: 'subsea', owner: 'Abdulrahman Al Kuwari', crs: 'EPSG:32639' },
  { id: 'dom-noc-geospatial', name: 'Geospatial', profile: 'geospatial', owner: 'Sophie Martin', crs: 'EPSG:4326' },
];

const NOC_USERS: Omit<UserRecord, 'tenantCode'>[] = [
  { id: 'usr-noc-admin', name: 'Mohammed Al Thani', email: 'mohammed.thani@noc.example', role: 'tenant.admin', domain: 'Asset Registry' },
  { id: 'usr-noc-eng-lead', name: 'Jean-Pierre Dupont', email: 'jp.dupont@noc.example', role: 'domain.owner', domain: 'Plant Engineering' },
  { id: 'usr-noc-subsea-lead', name: 'Abdulrahman Al Kuwari', email: 'a.kuwari@noc.example', role: 'domain.owner', domain: 'Subsea' },
  { id: 'usr-noc-gis-lead', name: 'Sophie Martin', email: 'sophie.martin@noc.example', role: 'domain.owner', domain: 'Geospatial' },
  { id: 'usr-noc-gallaf-analyst', name: 'Hassan Al Marri', email: 'hassan.marri@noc.example', role: 'project.creator', domain: 'Plant Engineering' },
  { id: 'usr-noc-platform-eng', name: 'Claire Lefebvre', email: 'claire.lefebvre@noc.example', role: 'project.editor', domain: 'Plant Engineering' },
  { id: 'usr-noc-ruya-lead', name: 'Nasser Al Dosari', email: 'nasser.dosari@noc.example', role: 'project.creator', domain: 'Asset Registry' },
  { id: 'usr-noc-viewer', name: 'Khalid Al Emadi', email: 'khalid.emadi@noc.example', role: 'project.viewer', domain: 'Geospatial' },
];

const ONGC_DOMAINS: Omit<DomainRecord, 'tenantCode'>[] = [
  { id: 'dom-ongc-asset-registry', name: 'Asset Registry', profile: 'asset', owner: 'Rajesh Sharma', crs: 'EPSG:32643' },
  { id: 'dom-ongc-plant-engineering', name: 'Plant Engineering', profile: 'engineering', owner: 'Priya Patel', crs: 'linear' },
  { id: 'dom-ongc-subsea', name: 'Subsea', profile: 'subsea', owner: 'Vikram Singh', crs: 'EPSG:32643' },
  { id: 'dom-ongc-geospatial', name: 'Geospatial', profile: 'geospatial', owner: 'Anita Deshmukh', crs: 'EPSG:4326' },
  { id: 'dom-ongc-reservoir', name: 'Reservoir Engineering', profile: 'engineering', owner: 'Suresh Kumar', crs: 'linear' },
];

const ONGC_USERS: Omit<UserRecord, 'tenantCode'>[] = [
  { id: 'usr-ongc-admin', name: 'Rajesh Sharma', email: 'rajesh.sharma@ongc.example', role: 'tenant.admin', domain: 'Asset Registry' },
  { id: 'usr-ongc-eng-lead', name: 'Priya Patel', email: 'priya.patel@ongc.example', role: 'domain.owner', domain: 'Plant Engineering' },
  { id: 'usr-ongc-subsea-lead', name: 'Vikram Singh', email: 'vikram.singh@ongc.example', role: 'domain.owner', domain: 'Subsea' },
  { id: 'usr-ongc-gis-lead', name: 'Anita Deshmukh', email: 'anita.deshmukh@ongc.example', role: 'domain.owner', domain: 'Geospatial' },
  { id: 'usr-ongc-reservoir-lead', name: 'Suresh Kumar', email: 'suresh.kumar@ongc.example', role: 'domain.owner', domain: 'Reservoir Engineering' },
  { id: 'usr-ongc-mumbai-eng', name: 'Deepak Nair', email: 'deepak.nair@ongc.example', role: 'project.creator', domain: 'Plant Engineering' },
  { id: 'usr-ongc-kg-analyst', name: 'Meera Reddy', email: 'meera.reddy@ongc.example', role: 'project.creator', domain: 'Subsea' },
  { id: 'usr-ongc-asset-mgr', name: 'Arun Gupta', email: 'arun.gupta@ongc.example', role: 'asset.manager', domain: 'Asset Registry' },
  { id: 'usr-ongc-viewer', name: 'Kavita Joshi', email: 'kavita.joshi@ongc.example', role: 'project.viewer', domain: 'Geospatial' },
  { id: 'usr-ongc-basin-eng', name: 'Rohit Mehta', email: 'rohit.mehta@ongc.example', role: 'project.editor', domain: 'Reservoir Engineering' },
];

const SAMPLE_TENANTS: SampleTenant[] = [
  { code: 'ADNOC', label: 'ADNOC', region: 'Abu Dhabi', classification: 'confidential', units: 'metric', assets: ADNOC_ASSETS, domains: ADNOC_DOMAINS, users: ADNOC_USERS },
  { code: 'NOC_QATAR', label: 'North Oil Company Qatar', region: 'Qatar', classification: 'confidential', units: 'metric', assets: NOC_QATAR_ASSETS, domains: NOC_DOMAINS, users: NOC_USERS },
  { code: 'ONGC', label: 'ONGC', region: 'India', classification: 'confidential', units: 'metric', assets: ONGC_ASSETS, domains: ONGC_DOMAINS, users: ONGC_USERS },
];

/* ── Helpers ────────────────────────────────────── */

function flatten(nodes: AssetTreeNode[]): AssetTreeNode[] {
  return nodes.flatMap((n) => [n, ...flatten(n.children ?? [])]);
}

function downloadText(filename: string, content: string, mime = 'text/plain') {
  const blob = new Blob([content], { type: mime });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function treeToCsvRows(tenantCode: string, nodes: AssetTreeNode[], parentId = ''): string[] {
  const rows: string[] = [];
  for (const n of nodes) {
    rows.push([
      'asset', tenantCode, n.id, parentId, n.label,
      '', '', '', '', '', '', '', '',
      String(n.lor ?? ''), n.node_type ?? '', '', '', '', '', n.path ?? '',
    ].join(','));
    if (n.children?.length) {
      rows.push(...treeToCsvRows(tenantCode, n.children, n.id));
    }
  }
  return rows;
}

/* ── Component ─────────────────────────────────── */

export function PlatformLanding() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<ScreenId>('overview');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [loading, setLoading] = useState('');

  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [activeTenantId, setActiveTenantId] = useState('');
  const [tree, setTree] = useState<AssetTreeNode[]>([]);
  const [allTrees, setAllTrees] = useState<Record<string, AssetTreeNode[]>>({});

  const [domains, setDomains] = useState<DomainRecord[]>([]);
  const [users, setUsers] = useState<UserRecord[]>([]);

  const [tenantName, setTenantName] = useState('');
  const [tenantCode, setTenantCode] = useState('');
  const [tenantRegion, setTenantRegion] = useState('EU North');
  const [tenantClassification, setTenantClassification] = useState('internal');
  const [tenantUnits, setTenantUnits] = useState('metric');

  const [domainName, setDomainName] = useState('');
  const [domainProfile, setDomainProfile] = useState('asset');
  const [domainOwner, setDomainOwner] = useState('');
  const [domainCrs, setDomainCrs] = useState('');

  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userRole, setUserRole] = useState('tenant.admin');
  const [userDomain, setUserDomain] = useState('');

  const csvInputRef = useRef<HTMLInputElement>(null);

  const activeTenant = useMemo(
    () => tenants.find((t) => t.id === activeTenantId) ?? null,
    [tenants, activeTenantId],
  );

  const allAssets = useMemo(() => flatten(tree), [tree]);
  const lor3plus = useMemo(
    () => allAssets.filter((a) => (a.lor ?? 3) >= 3).length,
    [allAssets],
  );

  const activeDomains = useMemo(
    () => {
      const code = activeTenant?.code;
      return code ? domains.filter((d) => d.tenantCode === code) : [];
    },
    [domains, activeTenant],
  );

  const activeUsers = useMemo(
    () => {
      const code = activeTenant?.code;
      return code ? users.filter((u) => u.tenantCode === code) : [];
    },
    [users, activeTenant],
  );

  const totalAssets = useMemo(() => {
    let count = 0;
    for (const t of Object.values(allTrees)) count += flatten(t).length;
    return count;
  }, [allTrees]);

  const totalLor3 = useMemo(() => {
    let count = 0;
    for (const t of Object.values(allTrees)) count += flatten(t).filter((a) => (a.lor ?? 3) >= 3).length;
    return count;
  }, [allTrees]);

  const loadTenants = useCallback(async () => {
    try {
      const rows = await listTenants();
      setTenants(rows);
      if (rows.length > 0 && !rows.find((t) => t.id === activeTenantId)) {
        setActiveTenantId(rows[0].id);
      }
    } catch {
      setTenants([]);
    }
  }, [activeTenantId]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  useEffect(() => {
    if (!activeTenantId) { setTree([]); return; }
    fetchAssetTree(activeTenantId).then((t) => {
      setTree(t);
      setAllTrees((prev) => ({ ...prev, [activeTenantId]: t }));
    }).catch(() => setTree([]));
  }, [activeTenantId]);

  useEffect(() => {
    for (const t of tenants) {
      if (!allTrees[t.id]) {
        fetchAssetTree(t.id).then((nodes) => {
          setAllTrees((prev) => ({ ...prev, [t.id]: nodes }));
        }).catch(() => {});
      }
    }
  }, [tenants, allTrees]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  function showScreen(id: ScreenId) {
    if (id === 'assets') { navigate('/admin/estate'); return; }
    setScreen(id);
    setDrawerOpen(false);
  }

  async function handleCreateTenant(e: React.FormEvent) {
    e.preventDefault();
    if (!tenantName.trim() || !tenantCode.trim()) return;
    try {
      const row = await createTenant({
        code: tenantCode.trim(),
        label: tenantName.trim(),
        region: tenantRegion,
        classification: tenantClassification,
        units: tenantUnits,
        created_by: 'platform-admin',
      });
      await loadTenants();
      setActiveTenantId(row.id);
      setTenantName('');
      setTenantCode('');
      setToast(`Created tenant "${row.label}"`);
    } catch (err) {
      setToast(`Error: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  function handleCreateDomain(e: React.FormEvent) {
    e.preventDefault();
    if (!activeTenant || !domainName.trim()) return;
    const id = `dom-${activeTenant.code.toLowerCase()}-${domainName.trim().toLowerCase().replace(/\s+/g, '-')}`;
    if (domains.find((d) => d.id === id)) {
      setToast('Domain already exists');
      return;
    }
    setDomains((prev) => [...prev, {
      id,
      tenantCode: activeTenant.code,
      name: domainName.trim(),
      profile: domainProfile,
      owner: domainOwner.trim(),
      crs: domainCrs.trim(),
    }]);
    setDomainName('');
    setDomainOwner('');
    setDomainCrs('');
    setToast(`Added domain "${domainName.trim()}"`);
  }

  function handleCreateUser(e: React.FormEvent) {
    e.preventDefault();
    if (!activeTenant || !userName.trim()) return;
    const id = `usr-${activeTenant.code.toLowerCase()}-${userName.trim().toLowerCase().replace(/\s+/g, '-')}`;
    setUsers((prev) => [...prev, {
      id,
      tenantCode: activeTenant.code,
      name: userName.trim(),
      email: userEmail.trim(),
      role: userRole,
      domain: userDomain,
    }]);
    setUserName('');
    setUserEmail('');
    setToast(`Added user "${userName.trim()}" to ${activeTenant.label}`);
  }

  async function loadSampleTenants(samples: SampleTenant[]) {
    setLoading('Loading sample data...');
    try {
      let lastTenantId = '';
      const newDomains: DomainRecord[] = [...domains];
      const newUsers: UserRecord[] = [...users];

      for (const s of samples) {
        const exists = tenants.find((t) => t.code === s.code);
        let tenantId: string;
        if (exists) {
          tenantId = exists.id;
        } else {
          const row = await createTenant({
            code: s.code,
            label: s.label,
            region: s.region,
            classification: s.classification,
            units: s.units,
            created_by: 'platform-admin',
          });
          tenantId = row.id;
        }
        lastTenantId = tenantId;

        const existingTree = await fetchAssetTree(tenantId);
        const existingIds = new Set(flatten(existingTree).map((a) => a.label));

        const idMap: Record<string, string> = {};
        for (const a of s.assets) {
          if (existingIds.has(a.label)) continue;
          const parentId = a.parentId ? idMap[a.parentId] ?? null : null;
          try {
            const created: AssetShellRecord = await createAsset(tenantId, {
              label: a.label,
              lor: a.lor,
              parent_id: parentId,
              node_type: a.nodeType,
            });
            idMap[a.id] = created.id;
          } catch {
            /* skip duplicates */
          }
        }

        for (const d of s.domains) {
          if (!newDomains.find((x) => x.id === d.id && x.tenantCode === s.code)) {
            newDomains.push({ ...d, tenantCode: s.code });
          }
        }
        for (const u of s.users) {
          if (!newUsers.find((x) => x.id === u.id && x.tenantCode === s.code)) {
            newUsers.push({ ...u, tenantCode: s.code });
          }
        }
      }

      setDomains(newDomains);
      setUsers(newUsers);
      await loadTenants();
      if (lastTenantId) setActiveTenantId(lastTenantId);
      setToast(`Loaded ${samples.length} sample tenant(s): ${samples.map((s) => s.label).join(', ')}`);
    } catch (err) {
      setToast(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading('');
    }
  }

  function exportCsv() {
    const header = 'recordType,tenantCode,id,parentId,label,owner,email,role,domainName,profile,classification,region,units,lor,nodeType,axis,boundaryOwner,functionalParentId,crossRefId,path';
    const lines = [header];
    for (const t of tenants) {
      lines.push(['tenant', t.code, t.id, '', t.label, '', '', '', '', '', t.classification, t.region ?? '', t.units].join(','));
      const treeNodes = allTrees[t.id] ?? [];
      lines.push(...treeToCsvRows(t.code, treeNodes));
    }
    downloadText('geosoft-hierarchy-export.csv', lines.join('\n'), 'text/csv');
    setToast('Exported hierarchy CSV');
  }

  function exportJson() {
    const state = {
      tenants: tenants.map((t) => ({
        id: t.id,
        code: t.code,
        label: t.label,
        region: t.region,
        classification: t.classification,
        units: t.units,
      })),
      trees: Object.fromEntries(
        tenants.map((t) => [t.code, allTrees[t.id] ?? []]),
      ),
      exportedAt: new Date().toISOString(),
    };
    downloadText('geosoft-state-export.json', JSON.stringify(state, null, 2), 'application/json');
    setToast('Exported state JSON');
  }

  async function handleCsvImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const lines = text.split('\n').filter((l) => l.trim());
    if (lines.length < 2) { setToast('CSV file is empty'); return; }

    setLoading('Importing CSV...');
    try {
      const rows = lines.slice(1).map((l) => l.split(','));
      const tenantRows = rows.filter((r) => r[0] === 'tenant');
      const assetRows = rows.filter((r) => r[0] === 'asset');

      for (const r of tenantRows) {
        const code = r[1]?.trim();
        const label = r[4]?.trim() || code;
        const region = r[11]?.trim() || '';
        const classification = r[10]?.trim() || 'internal';
        const units = r[12]?.trim() || 'metric';
        if (!code) continue;
        const exists = tenants.find((t) => t.code === code);
        if (!exists) {
          await createTenant({ code, label, region, classification, units, created_by: 'csv-import' });
        }
      }
      await loadTenants();
      const refreshed = await listTenants();

      for (const r of assetRows) {
        const tenantCode = r[1]?.trim();
        const label = r[4]?.trim();
        const lor = parseInt(r[13]) || 0;
        const nodeType = r[14]?.trim() || 'generic';
        if (!tenantCode || !label) continue;
        const tenant = refreshed.find((t) => t.code === tenantCode);
        if (!tenant) continue;
        try {
          await createAsset(tenant.id, { label, lor, node_type: nodeType });
        } catch {
          /* skip duplicates */
        }
      }
      await loadTenants();
      setToast(`Imported from ${file.name}`);
    } catch (err) {
      setToast(`Import error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading('');
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  }

  return (
    <div className="gf-shell">
      {/* ── Header ── */}
      <header className="gf-header">
        <div className="gf-header-left">
          <button
            className="gf-menu-btn"
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation"
          >
            <span className="material-symbols-outlined">menu</span>
          </button>
          <div className="gf-brand">
            <div className="gf-brand-mark">GS</div>
            <span className="gf-brand-text">GeoSoft</span>
          </div>
        </div>

        <nav className="gf-header-nav">
          {HEADER_TABS.map((tab) => (
            <button
              key={tab.id}
              className={`gf-nav-tab${screen === tab.id ? ' active' : ''}`}
              onClick={() => showScreen(tab.id)}
            >
              <span className="material-symbols-outlined">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="gf-header-right">
          <select
            className="gf-tenant-select"
            value={activeTenantId}
            onChange={(e) => setActiveTenantId(e.target.value)}
          >
            {tenants.length === 0 && <option value="">No tenants</option>}
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
        </div>
      </header>

      {/* ── Action bar (V5 hierarchy import/export) ── */}
      <div className="gf-action-bar">
        <button
          className="gf-btn primary"
          disabled={!!loading}
          onClick={() => loadSampleTenants(SAMPLE_TENANTS)}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
          Load 3 sample tenants
        </button>
        <button
          className="gf-btn"
          disabled={!!loading}
          onClick={() => loadSampleTenants([SAMPLE_TENANTS[0]])}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
          Load clean ADNOC only
        </button>
        <button className="gf-btn" onClick={exportCsv}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>upload_file</span>
          Download CSV
        </button>
        <button className="gf-btn" onClick={() => csvInputRef.current?.click()}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>file_open</span>
          Import CSV
        </button>
        <button className="gf-btn" onClick={exportJson}>
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>data_object</span>
          Export JSON
        </button>
        {loading && <span className="gf-badge accent">{loading}</span>}
      </div>
      <input ref={csvInputRef} type="file" accept=".csv" hidden onChange={handleCsvImport} />

      {/* ── Drawer overlay ── */}
      <div
        className={`gf-drawer-scrim${drawerOpen ? ' open' : ''}`}
        onClick={() => setDrawerOpen(false)}
      />
      <aside className={`gf-drawer${drawerOpen ? ' open' : ''}`}>
        <div className="gf-drawer-header">
          <div className="gf-brand">
            <div className="gf-brand-mark">GS</div>
            <span className="gf-brand-text">GeoSoft</span>
          </div>
          <button
            className="gf-drawer-close"
            type="button"
            onClick={() => setDrawerOpen(false)}
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>
        <nav className="gf-drawer-nav">
          {DRAWER_ITEMS.map((item) => (
            <button
              key={item.id}
              className={`gf-drawer-item${screen === item.id ? ' active' : ''}`}
              onClick={() => showScreen(item.id)}
            >
              <span className="material-symbols-outlined">{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>
        <div className="gf-drawer-footer">
          Tenant installation is not the same as an analysis project.
          Assets are canonical. Analysis projects reference them.
        </div>
      </aside>

      {/* ── Tenant context strip ── */}
      {activeTenant && (
        <div className="gf-tenant-strip">
          <div>
            <h3>{activeTenant.label}</h3>
            <p>
              {activeTenant.region || 'No region'} ·{' '}
              {activeTenant.classification} · {activeTenant.units}
            </p>
          </div>
          <div className="gf-row">
            <span className="gf-chip">{allAssets.length} assets</span>
            <span className="gf-chip">{lor3plus} LoR3+</span>
          </div>
        </div>
      )}

      {/* ── Main content ── */}
      <main className="gf-main">
        {/* ═══ Overview ═══ */}
        <section className={`gf-screen${screen === 'overview' ? ' active' : ''}`}>
          <div className="gf-content">
            {tenants.length === 0 ? (
              <div className="gf-card" style={{ textAlign: 'center', padding: tokens.space[7] }}>
                <span className="material-symbols-outlined" style={{ fontSize: tokens.space[7], color: 'var(--accent)', marginBottom: tokens.space[3] }}>rocket_launch</span>
                <h2 style={{ fontSize: tokens.font.size.xl, fontWeight: 400 }}>Welcome to GeoSoft Platform</h2>
                <p style={{ color: 'var(--ink-secondary)', marginTop: tokens.space[2], maxWidth: tokens.layout.standaloneStateNarrow, marginLeft: 'auto', marginRight: 'auto' }}>
                  No tenants installed yet. Load sample data or create your first tenant to explore the platform.
                </p>
                <div className="gf-row" style={{ justifyContent: 'center', marginTop: 24, gap: 12 }}>
                  <button className="gf-btn primary" onClick={() => loadSampleTenants(SAMPLE_TENANTS)} disabled={!!loading}>
                    Load 3 sample tenants
                  </button>
                  <button className="gf-btn" onClick={() => setScreen('installation')}>
                    Create first tenant
                  </button>
                </div>
              </div>
            ) : (
              <div className="gf-stack" style={{ gap: 24 }}>
                {/* Platform-wide stats */}
                <div className="gf-grid-4">
                  <div className="gf-stat-card">
                    <div className="stat-n">{tenants.length}</div>
                    <div className="stat-l">Tenant installations</div>
                  </div>
                  <div className="gf-stat-card">
                    <div className="stat-n">{totalAssets}</div>
                    <div className="stat-l">Total assets (all tenants)</div>
                  </div>
                  <div className="gf-stat-card">
                    <div className="stat-n">{totalLor3}</div>
                    <div className="stat-l">LoR3+ addressable shells</div>
                  </div>
                  <div className="gf-stat-card">
                    <div className="stat-n">{domains.length}</div>
                    <div className="stat-l">Domains (all tenants)</div>
                  </div>
                  <div className="gf-stat-card">
                    <div className="stat-n">{users.length}</div>
                    <div className="stat-l">Users (all tenants)</div>
                  </div>
                  <div className="gf-stat-card">
                    <div className="stat-n">—</div>
                    <div className="stat-l">Active projects</div>
                  </div>
                </div>

                {/* Tenant selector pills */}
                <div className="gf-card">
                  <div className="gf-card-header">
                    <div>
                      <h3>Tenant installations</h3>
                      <p>Select a tenant to view its estate. Stats below reflect the active tenant.</p>
                    </div>
                    <button className="gf-btn small" onClick={() => setScreen('installation')}>
                      <span className="material-symbols-outlined" style={{ fontSize: 14 }}>add</span>
                      New tenant
                    </button>
                  </div>
                  <div className="gf-tenant-pills">
                    {tenants.map((t) => {
                      const treeForT = allTrees[t.id] ?? [];
                      const assetCount = flatten(treeForT).length;
                      const domCount = domains.filter((d) => d.tenantCode === t.code).length;
                      const usrCount = users.filter((u) => u.tenantCode === t.code).length;
                      return (
                        <div
                          key={t.id}
                          className={`gf-tenant-pill${t.id === activeTenantId ? ' active' : ''}`}
                          onClick={() => setActiveTenantId(t.id)}
                        >
                          <strong>{t.label}</strong>
                          <small>{t.region || 'no region'} · {assetCount} assets · {domCount} domains · {usrCount} users</small>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Active tenant stats */}
                {activeTenant && (
                  <>
                    <div className="gf-grid-4">
                      <div className="gf-stat-card lor0">
                        <div className="stat-n">{allAssets.filter((a) => (a.lor ?? 0) === 0).length}</div>
                        <div className="stat-l">LoR0 · Portfolio / region</div>
                      </div>
                      <div className="gf-stat-card lor1">
                        <div className="stat-n">{allAssets.filter((a) => (a.lor ?? 0) === 1).length}</div>
                        <div className="stat-l">LoR1 · Business area</div>
                      </div>
                      <div className="gf-stat-card lor2">
                        <div className="stat-n">{allAssets.filter((a) => (a.lor ?? 0) === 2).length}</div>
                        <div className="stat-l">LoR2 · Field / location</div>
                      </div>
                      <div className="gf-stat-card lor3">
                        <div className="stat-n">{allAssets.filter((a) => (a.lor ?? 0) >= 3).length}</div>
                        <div className="stat-l">LoR3+ · Addressable shells</div>
                      </div>
                    </div>

                    {/* LoR distribution detail */}
                    <details className="gf-collapsible" open>
                      <summary>
                        LoR distribution — {activeTenant.label}
                        <span className="gf-badge">{allAssets.length} total</span>
                      </summary>
                      <div>
                        <div className="gf-grid-4" style={{ marginTop: 8 }}>
                          {LOR_BANDS.map((b) => {
                            const c = allAssets.filter((a) =>
                              b.l === 5 ? (a.lor ?? 3) >= 5 : (a.lor ?? 3) === b.l,
                            ).length;
                            return (
                              <div key={b.l} className={`gf-stat-card lor${b.l}`}>
                                <div className="stat-n">{c}</div>
                                <div className="stat-l">LoR{b.l} — {b.n}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </details>

                    {/* Workflow cards */}
                    <div className="gf-grid-3">
                      <div className="gf-step-card" style={{ cursor: 'pointer' }} onClick={() => setScreen('installation')}>
                        <div className="gf-step-num">1</div>
                        <div>
                          <h3>Tenant installation</h3>
                          <p>Create client environment, org structure, domains, and asset hierarchy.</p>
                        </div>
                      </div>
                      <div className="gf-step-card" style={{ cursor: 'pointer' }} onClick={() => showScreen('assets')}>
                        <div className="gf-step-num">2</div>
                        <div>
                          <h3>Asset library</h3>
                          <p>Canonical digital estate: locations, platforms, decks, systems, and data layers.</p>
                        </div>
                      </div>
                      <div className="gf-step-card" style={{ cursor: 'pointer' }} onClick={() => setScreen('projects')}>
                        <div className="gf-step-num">3</div>
                        <div>
                          <h3>Analysis project</h3>
                          <p>User work area referencing gTwin layers, owns annotations and results.</p>
                        </div>
                      </div>
                    </div>

                    {/* Quick actions + Estate tree */}
                    <div className="gf-grid-2">
                      <details className="gf-collapsible" open>
                        <summary>
                          Estate tree — {activeTenant.label}
                          <span className="gf-badge">{allAssets.length} nodes</span>
                        </summary>
                        <div>
                          <div className="gf-tree">
                            {tree.length === 0 ? (
                              <p style={{ fontSize: 13, color: 'var(--ink-muted)' }}>
                                No assets yet. Load sample data or use the hierarchy explorer.
                              </p>
                            ) : (
                              tree.map((node) => <TreeNode key={node.id} node={node} />)
                            )}
                          </div>
                          {tree.length > 0 && (
                            <div className="gf-row" style={{ marginTop: 12 }}>
                              <button className="gf-btn small" onClick={() => showScreen('assets')}>
                                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>open_in_new</span>
                                Full hierarchy explorer
                              </button>
                            </div>
                          )}
                        </div>
                      </details>

                      <div className="gf-card">
                        <div className="gf-card-header">
                          <div>
                            <h3>Platform quick actions</h3>
                            <p>Common workflows for platform administrators.</p>
                          </div>
                        </div>
                        <div className="gf-stack" style={{ gap: 8 }}>
                          <button className="gf-btn" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => setScreen('installation')}>
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>apartment</span>
                            Create new tenant installation
                          </button>
                          <button className="gf-btn" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => showScreen('assets')}>
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>account_tree</span>
                            Open hierarchy explorer
                          </button>
                          <button className="gf-btn" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={() => navigate('/projects')}>
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>visibility</span>
                            Open viewer workspace
                          </button>
                          <button className="gf-btn" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={exportCsv}>
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>upload_file</span>
                            Export all tenants as CSV
                          </button>
                          <button className="gf-btn" style={{ width: '100%', justifyContent: 'flex-start' }} onClick={exportJson}>
                            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>data_object</span>
                            Export platform state as JSON
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Engineer contract summary */}
                    <details className="gf-collapsible">
                      <summary>
                        What engineers should implement
                        <span className="gf-badge">Expand</span>
                      </summary>
                      <div>
                        <div className="gf-stack">
                          <div className="gf-callout"><strong>Admin workflow:</strong> create tenant, orgs, domains, asset hierarchy, gTwins, and canonical layers.</div>
                          <div className="gf-callout"><strong>User workflow:</strong> create analysis project, select referenced layers, create/save work outputs.</div>
                          <div className="gf-callout"><strong>Data rule:</strong> user project work must not mutate canonical asset layers unless an explicit library operation is performed.</div>
                        </div>
                      </div>
                    </details>
                  </>
                )}
              </div>
            )}
          </div>
        </section>

        {/* ═══ Tenant Installation ═══ */}
        <section className={`gf-screen${screen === 'installation' ? ' active' : ''}`}>
          <div className="gf-content">
            <div className="gf-grid-2">
              <div className="gf-card">
                <div className="gf-card-header">
                  <div>
                    <h3>Create tenant installation</h3>
                    <p>Top-level client environment.</p>
                  </div>
                </div>
                <form className="gf-form" onSubmit={handleCreateTenant}>
                  <div className="gf-field-row">
                    <label>
                      Tenant name
                      <input value={tenantName} onChange={(e) => setTenantName(e.target.value)} placeholder="North Sea Energy" required />
                    </label>
                    <label>
                      Tenant code
                      <input value={tenantCode} onChange={(e) => setTenantCode(e.target.value)} placeholder="north-sea-energy" required />
                    </label>
                  </div>
                  <div className="gf-field-row">
                    <label>
                      Classification
                      <select value={tenantClassification} onChange={(e) => setTenantClassification(e.target.value)}>
                        <option>internal</option>
                        <option>restricted</option>
                        <option>confidential</option>
                      </select>
                    </label>
                    <label>
                      Data region
                      <select value={tenantRegion} onChange={(e) => setTenantRegion(e.target.value)}>
                        <option>EU North</option>
                        <option>Middle East</option>
                        <option>US East</option>
                        <option>On-prem</option>
                      </select>
                    </label>
                  </div>
                  <label>
                    Default units
                    <select value={tenantUnits} onChange={(e) => setTenantUnits(e.target.value)}>
                      <option>metric</option>
                      <option>imperial</option>
                      <option>survey-ft</option>
                    </select>
                  </label>
                  <button className="gf-btn primary" type="submit">Create tenant</button>
                </form>
              </div>

              <div className="gf-card">
                <div className="gf-card-header">
                  <div>
                    <h3>Tenants</h3>
                    <p>All platform tenants.</p>
                  </div>
                  <span className="gf-badge">{tenants.length}</span>
                </div>
                <div className="gf-table-wrap">
                  <table>
                    <thead>
                      <tr><th>Name</th><th>Code</th><th>Region</th><th>Class.</th><th>Units</th></tr>
                    </thead>
                    <tbody>
                      {tenants.length === 0 ? (
                        <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--ink-muted)', padding: 24 }}>No tenants yet.</td></tr>
                      ) : tenants.map((t) => (
                        <tr key={t.id} className={t.id === activeTenantId ? 'selected' : ''} style={{ cursor: 'pointer' }} onClick={() => setActiveTenantId(t.id)}>
                          <td><strong>{t.label}</strong></td>
                          <td>{t.code}</td>
                          <td>{t.region}</td>
                          <td>{t.classification}</td>
                          <td>{t.units}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ Domains and Users ═══ */}
        <section className={`gf-screen${screen === 'domains' ? ' active' : ''}`}>
          <div className="gf-content">
            {!activeTenant ? (
              <div className="gf-card" style={{ textAlign: 'center', padding: 48 }}>
                <h2 style={{ fontSize: 18, fontWeight: 400 }}>Create a tenant installation first</h2>
                <p style={{ color: 'var(--ink-secondary)', marginTop: 8 }}>Domains and users belong to a tenant.</p>
                <button className="gf-btn primary" style={{ marginTop: 16 }} onClick={() => setScreen('installation')}>Go to tenants</button>
              </div>
            ) : (
              <>
                <div className="gf-grid-2">
                  {/* Domain form */}
                  <div className="gf-card">
                    <div className="gf-card-header">
                      <div>
                        <h3>Add domain / department</h3>
                        <p>Domains define default behavior for assets and viewers.</p>
                      </div>
                    </div>
                    <form className="gf-form" onSubmit={handleCreateDomain}>
                      <div className="gf-field-row">
                        <label>
                          Domain name
                          <input value={domainName} onChange={(e) => setDomainName(e.target.value)} placeholder="Subsea Operations" required />
                        </label>
                        <label>
                          Domain profile
                          <select value={domainProfile} onChange={(e) => setDomainProfile(e.target.value)}>
                            <option value="asset">asset</option>
                            <option value="engineering">engineering</option>
                            <option value="subsea">subsea</option>
                            <option value="geospatial">geospatial</option>
                          </select>
                        </label>
                      </div>
                      <div className="gf-field-row">
                        <label>
                          Owner user
                          <input value={domainOwner} onChange={(e) => setDomainOwner(e.target.value)} placeholder="Mateo Silva" required />
                        </label>
                        <label>
                          Default CRS / frame
                          <input value={domainCrs} onChange={(e) => setDomainCrs(e.target.value)} placeholder="EPSG:32631 or linear" />
                        </label>
                      </div>
                      <button className="gf-btn primary" type="submit">Add domain</button>
                    </form>
                  </div>

                  {/* Domain table */}
                  <div className="gf-card">
                    <div className="gf-card-header">
                      <div>
                        <h3>Domains</h3>
                        <p>Department/domain registry for {activeTenant.label}.</p>
                      </div>
                      <span className="gf-badge">{activeDomains.length}</span>
                    </div>
                    <div className="gf-table-wrap">
                      <table>
                        <thead>
                          <tr><th>Domain</th><th>Profile</th><th>Owner</th><th>Default CRS</th></tr>
                        </thead>
                        <tbody>
                          {activeDomains.length === 0 ? (
                            <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--ink-muted)', padding: 24 }}>No domains for this tenant.</td></tr>
                          ) : activeDomains.map((d) => (
                            <tr key={d.id}>
                              <td><strong>{d.name}</strong><br /><span style={{ fontSize: 11, color: 'var(--ink-muted)', fontFamily: 'monospace' }}>{d.id}</span></td>
                              <td><span className="gf-badge accent">{d.profile}</span></td>
                              <td>{d.owner}</td>
                              <td>{d.crs || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="gf-grid-2" style={{ marginTop: 24 }}>
                  {/* User form */}
                  <div className="gf-card">
                    <div className="gf-card-header">
                      <div>
                        <h3>Add tenant user</h3>
                        <p>Users belong to {activeTenant.label} and can be assigned to a domain.</p>
                      </div>
                    </div>
                    <form className="gf-form" onSubmit={handleCreateUser}>
                      <div className="gf-field-row">
                        <label>
                          User name
                          <input value={userName} onChange={(e) => setUserName(e.target.value)} placeholder="Aisha Khan" required />
                        </label>
                        <label>
                          Email
                          <input value={userEmail} onChange={(e) => setUserEmail(e.target.value)} type="email" placeholder="aisha.khan@example.com" />
                        </label>
                      </div>
                      <div className="gf-field-row">
                        <label>
                          Role
                          <select value={userRole} onChange={(e) => setUserRole(e.target.value)}>
                            <option value="tenant.admin">tenant.admin</option>
                            <option value="domain.owner">domain.owner</option>
                            <option value="asset.manager">asset.manager</option>
                            <option value="project.creator">project.creator</option>
                            <option value="project.editor">project.editor</option>
                            <option value="project.viewer">project.viewer</option>
                          </select>
                        </label>
                        <label>
                          Domain / department
                          <select value={userDomain} onChange={(e) => setUserDomain(e.target.value)}>
                            <option value="">No domain assignment</option>
                            {activeDomains.map((d) => (
                              <option key={d.id} value={d.name}>{d.name} ({d.profile})</option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <button className="gf-btn primary" type="submit">Add tenant user</button>
                    </form>
                  </div>

                  {/* User table */}
                  <div className="gf-card">
                    <div className="gf-card-header">
                      <div>
                        <h3>Tenant users</h3>
                        <p>Users for {activeTenant.label}.</p>
                      </div>
                      <span className="gf-badge">{activeUsers.length}</span>
                    </div>
                    <div className="gf-table-wrap">
                      <table>
                        <thead>
                          <tr><th>User</th><th>Email</th><th>Role</th><th>Domain</th></tr>
                        </thead>
                        <tbody>
                          {activeUsers.length === 0 ? (
                            <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--ink-muted)', padding: 24 }}>No users for this tenant yet.</td></tr>
                          ) : activeUsers.map((u) => (
                            <tr key={u.id}>
                              <td><strong>{u.name}</strong></td>
                              <td>{u.email || <span style={{ color: 'var(--ink-muted)' }}>not set</span>}</td>
                              <td><span className="gf-badge">{u.role}</span></td>
                              <td>{u.domain || <span style={{ color: 'var(--ink-muted)' }}>unassigned</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* ═══ Layers (placeholder) ═══ */}
        <section className={`gf-screen${screen === 'layers' ? ' active' : ''}`}>
          <div className="gf-content">
            <div className="gf-card">
              <div className="gf-card-header">
                <div>
                  <h3>gTwins and layers</h3>
                  <p>Canonical data layers attached to LoR3 assets.</p>
                </div>
              </div>
              <div className="gf-callout">
                <strong>Coming next:</strong> Layer creation, import from gTwin service, asset-to-layer mapping.
              </div>
            </div>
          </div>
        </section>

        {/* ═══ Viewer (placeholder) ═══ */}
        <section className={`gf-screen${screen === 'viewer' ? ' active' : ''}`}>
          <div className="gf-content">
            <div className="gf-card">
              <div className="gf-card-header">
                <div>
                  <h3>Viewer workspace</h3>
                  <p>Scene hierarchy, viewer display, project package.</p>
                </div>
              </div>
              <div className="gf-row" style={{ marginTop: 16 }}>
                <button className="gf-btn primary" onClick={() => navigate('/projects')}>Open Projects Hub</button>
                <button className="gf-btn" onClick={() => showScreen('assets')}>Open estate</button>
              </div>
            </div>
          </div>
        </section>

        {/* ═══ Projects (placeholder) ═══ */}
        <section className={`gf-screen${screen === 'projects' ? ' active' : ''}`}>
          <div className="gf-content">
            <div className="gf-card">
              <div className="gf-card-header">
                <div>
                  <h3>Analysis projects</h3>
                  <p>User work areas referencing canonical layers.</p>
                </div>
              </div>
              <div className="gf-callout">
                <strong>Coming next:</strong> Project creation, reference layer selection, project table.
              </div>
            </div>
          </div>
        </section>

        {/* ═══ Handover ═══ */}
        <section className={`gf-screen${screen === 'handover' ? ' active' : ''}`}>
          <div className="gf-content">
            <div className="gf-grid-2">
              <div className="gf-card">
                <div className="gf-card-header">
                  <div>
                    <h3>Engineer handover contract</h3>
                    <p>What the final application must replicate.</p>
                  </div>
                </div>
                <div className="gf-stack">
                  <div className="gf-callout"><strong>Screen 1 — Tenant installation:</strong> Creates the permanent client environment, org structure, and admin hierarchy.</div>
                  <div className="gf-callout"><strong>Screen 2 — Domains and users:</strong> Define department ownership, domain defaults, and user roles.</div>
                  <div className="gf-callout"><strong>Screen 3 — Asset hierarchy:</strong> Organize the client estate in plant/asset/location patterns (LoR0-LoR3).</div>
                  <div className="gf-callout"><strong>Screen 4 — gTwins and layers:</strong> Attach canonical data layers to addressable asset shells.</div>
                  <div className="gf-callout"><strong>Screen 5 — User viewer:</strong> Opens the same hierarchy as a layer package for visualization.</div>
                  <div className="gf-callout"><strong>Screen 6 — Analysis projects:</strong> Reference canonical layers and own work files, annotations, and results.</div>
                </div>
              </div>

              <div className="gf-card">
                <div className="gf-card-header">
                  <div>
                    <h3>Platform status</h3>
                    <p>Current implementation state.</p>
                  </div>
                </div>
                <div className="gf-table-wrap">
                  <table>
                    <thead>
                      <tr><th>Capability</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>Tenant CRUD</td><td><span className="gf-badge accent">Live</span></td></tr>
                      <tr><td>Asset hierarchy (LoR0-LoR3)</td><td><span className="gf-badge accent">Live</span></td></tr>
                      <tr><td>Converter association</td><td><span className="gf-badge accent">Live</span></td></tr>
                      <tr><td>Sample data loading</td><td><span className="gf-badge accent">Live</span></td></tr>
                      <tr><td>CSV / JSON export</td><td><span className="gf-badge accent">Live</span></td></tr>
                      <tr><td>CSV import</td><td><span className="gf-badge accent">Live</span></td></tr>
                      <tr><td>Domains and users</td><td><span className="gf-badge accent">Live</span></td></tr>
                      <tr><td>Layer management</td><td><span className="gf-badge">Planned</span></td></tr>
                      <tr><td>Analysis projects</td><td><span className="gf-badge">Planned</span></td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {toast && <div className="gf-toast">{toast}</div>}
    </div>
  );
}

function TreeNode({ node }: { node: AssetTreeNode }) {
  return (
    <div className="gf-tree-node" data-lor={node.lor}>
      <strong>{node.label}</strong><br />
      <small>LoR{node.lor} · {node.node_type}{node.path ? ` · ${node.path}` : ''}</small>
      {node.children && node.children.length > 0 && (
        <div className="gf-tree" style={{ marginTop: 6, marginLeft: 12 }}>
          {node.children.map((c) => <TreeNode key={c.id} node={c} />)}
        </div>
      )}
    </div>
  );
}
